import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import {
    AddJobOpening,
    DeleteJobOpening,
    GetAllJobOpening,
    GetApplicationsByJob,
    GetJobOpening,
    GetJobOpeningBySlug,
    GetSingleJobOpening,
    SelectJobOpening,
    UpdateJobOpening,
    UpdateJobOpeningStatus
} from './job-opening.actions';
import { JobOpeningService } from './job-opening.service';
import { catchError, tap } from 'rxjs';
import { IJobOpeningFormState, IJobOpeningModel, IJobOpeningRequest } from './job-opening.types';
import { IApplicationDTO } from '../applications/application.interface';

export interface JobOpeningStateModel {
    jobOpenings: IJobOpeningModel[];
    jobOpening: IJobOpeningModel | undefined;
    applications: IApplicationDTO[];
    SingleJobOpening: IJobOpeningRequest | undefined;
    SelectedJobOpening: IJobOpeningFormState | null;
    loading: boolean;
    loadingSingleJob: boolean;
    error: string | null;
}

const defaults = {
    jobOpenings: [],
    jobOpening: undefined,
    SingleJobOpening: undefined,
    SelectedJobOpening: null,
    loading: false,
    loadingSingleJob: false,
    applications: [],
    error: null
};

@State<JobOpeningStateModel>({
    name: 'jobOpening',
    defaults
})
@Injectable()
export class JobOpeningState {
    jobOpeningService: JobOpeningService = inject(JobOpeningService);
    private store: Store = inject(Store);

    @Selector()
    static getJobOpenings(state: JobOpeningStateModel) {
        return state.jobOpenings;
    }

    @Selector()
    static getJobOpening(state: JobOpeningStateModel) {
        return state.jobOpening;
    }

    @Selector()
    static getSingleJobOpening(state: JobOpeningStateModel) {
        return state.SingleJobOpening;
    }

    @Selector()
    static getSelectedJobOpening(state: JobOpeningStateModel) {
        return state.SelectedJobOpening;
    }

    @Selector()
    static getLoadingJob(state: JobOpeningStateModel) {
        return state.loadingSingleJob;
    }

    @Selector()
    static getLoading(state: JobOpeningStateModel) {
        return state.loading;
    }

    @Selector()
    static getError(state: JobOpeningStateModel) {
        return state.error;
    }

    @Selector()
    static getApplicationsByJob(state: JobOpeningStateModel) {
        return state.applications;
    }

    @Action(SelectJobOpening)
    selectEntity(ctx: StateContext<JobOpeningStateModel>, action: SelectJobOpening) {
        const state = ctx.getState();
        if (action.payload == null) {
            ctx.patchState({
                SelectedJobOpening: null
            });
            return;
        }
        const payload = action.payload;
        const selected: IJobOpeningFormState = {
            id: payload.id,
            jobInformation: {
                title: payload.title,
                jobDescriptionId: payload.jobDescriptionId,
                employmentType: payload.employmentType,
                hiringLeadId: payload.hiringLeadId,
                compensation: payload.compensation,
                jobCode: payload.jobCode,
                location: payload.location,
                jobType: payload.jobType,
                experienceLevel: payload.experienceLevel,
                finished: !!payload.experienceLevel
            },
            applicationDetails: {
                additionalFields: payload.additionalFields || [],
                finished: !!payload.additionalFields && payload.additionalFields.length > 0
            },
            department: {
                role: payload.role,
                department: payload.department,
                finished: !!payload.department
            },
            jobBoard: {
                status: payload.status,
                finished: payload?.status !== 'Draft'
            }
        };
        ctx.patchState({
            SelectedJobOpening: selected
        });
    }

    @Action(GetAllJobOpening)
    get(ctx: StateContext<JobOpeningStateModel>, action: GetAllJobOpening) {
        const state = ctx.getState();

        ctx.patchState({ loading: true });

        return this.jobOpeningService.getJobs().pipe(
            tap((response: any) => {
                ctx.patchState({
                    jobOpenings: response,
                    loading: false,
                    error: null
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(GetApplicationsByJob)
    getjobApplications(ctx: StateContext<JobOpeningStateModel>, action: GetApplicationsByJob) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.jobOpeningService.getApplByJob(action.id).pipe(
            tap((res: any) => {
                ctx.patchState({
                    applications: res,
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetJobOpening)
    getSingle(ctx: StateContext<JobOpeningStateModel>, action: GetJobOpening) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.jobOpeningService.getSingle(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    jobOpening: res as IJobOpeningModel,
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetJobOpeningBySlug)
    getSingleSlug(ctx: StateContext<JobOpeningStateModel>, action: GetJobOpeningBySlug) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.jobOpeningService.getSingleSlug(action.slug).pipe(
            tap((res) => {
                ctx.patchState({
                    jobOpening: res as IJobOpeningModel,
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetSingleJobOpening)
    getSingleJob(ctx: StateContext<JobOpeningStateModel>, action: GetSingleJobOpening) {
        const state = ctx.getState();
        ctx.patchState({ loadingSingleJob: true });
        return this.jobOpeningService.getSingle(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    SingleJobOpening: res as IJobOpeningRequest,
                    loadingSingleJob: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loadingSingleJob: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(AddJobOpening)
    createJob(ctx: StateContext<JobOpeningStateModel>, action: AddJobOpening) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.jobOpeningService.createJob(action.payload).pipe(
            tap((response: IJobOpeningModel) => {
                console.log('RESPONSE fOr add job', response);
                ctx.patchState({
                    jobOpenings: [response as IJobOpeningModel, ...state.jobOpenings],
                    jobOpening: response as IJobOpeningModel,
                    loading: false,
                    error: null
                });
                this.store.dispatch(new SelectJobOpening(response));
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(UpdateJobOpening)
    updateJob(ctx: StateContext<JobOpeningStateModel>, action: UpdateJobOpening) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.jobOpeningService.updateJob(action.payload).pipe(
            tap((res: IJobOpeningModel) => {
                console.log('Update Job', res);

                ctx.patchState({
                    jobOpenings: state.jobOpenings.map((job: IJobOpeningModel) =>
                        job.id === action.payload.id ? { ...job, ...res } : job
                    ),
                    jobOpening: res as IJobOpeningModel,
                    loading: false,
                    error: null
                });
                this.store.dispatch(new SelectJobOpening(res));
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(UpdateJobOpeningStatus)
    updateJobStatus(ctx: StateContext<JobOpeningStateModel>, action: UpdateJobOpeningStatus) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.jobOpeningService.updateJobStatus(action.payload).pipe(
            tap((res: IJobOpeningModel) => {
                console.log('Update Status', res);
                ctx.patchState({
                    jobOpenings: state.jobOpenings.map((job: IJobOpeningModel) =>
                        job.id === action.payload.id ? { ...job, ...res } : job
                    ),
                    jobOpening: res as IJobOpeningModel,
                    loading: false,
                    error: null
                });
                this.store.dispatch(new SelectJobOpening(res));
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(DeleteJobOpening)
    deleteJob(ctx: StateContext<JobOpeningStateModel>, action: DeleteJobOpening) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.jobOpeningService.deleteJob(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    jobOpenings: state.jobOpenings.filter((job) => job.id !== action.id),
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }
}
